/**
 * Recipe Metrics Calculator
 *
 * Port of the mes-pekara logic (files/recipes.py -> load_recipes SQL +
 * calc_recipe_metrics). Produces the four metrics shown on the recipe screen:
 *   - Ukupni PEK%        (total_pct)        sum of all baker percentages
 *   - Brašno PEK%        (flour_total)      flour incl. enzymes + starter flour
 *   - Hidracija faza 1   (hydration_phase_1) water WITHOUT "voda 2"
 *   - Ukupna hidracija   (total_hydration)   water WITH "voda 2"
 *
 * Water is split into three buckets, exactly like calc_recipe_metrics:
 *   - voda1         regular water (ingredient type "Voda", name not "voda 2")
 *   - voda2         water explicitly named "voda 2"
 *   - voda_starter  water coming from semi-finished/child recipes (starter,
 *                   biga, yudane, ...), excluding seed_mix
 *
 *   hidracija faza 1 = (voda1 + voda_starter) / brasno * 100
 *   ukupna hidracija = (voda1 + voda2 + voda_starter) / brasno * 100
 */

import { RecipeIngredient, Ingredient, Recipe } from "../types/mes";

const norm = (s?: string | null) => (s || "").toLowerCase().trim();

/**
 * Resolve an ingredient's type name. The recipe API embeds nested
 * (child-recipe) ingredients with only the IngredientType lookup relation and
 * WITHOUT the denormalized `IngredientTypeName` (that is only added by the
 * backend's serializeIngredient, e.g. on the flat ingredients endpoint). Fall
 * back to the relation's Name/Code so flour from semi-finished products is
 * classified correctly.
 */
const resolveType = (ing?: Ingredient | null): string | null => {
  if (!ing) return null;
  return (
    ing.IngredientTypeName ??
    ing.IngredientType_IngredientToIngredientType?.Name ??
    ing.IngredientType_IngredientToIngredientType?.Code ??
    null
  );
};

const isFlour = (type?: string | null) => {
  const t = norm(type);
  return t === "flour" || t === "brasno" || t === "brašno" || t.includes("brasno") || t.includes("flour");
};

const isEnzyme = (type?: string | null) => {
  const t = norm(type);
  return t === "enzim" || t === "enzimi" || t.includes("enzim");
};

const isWater = (type?: string | null, name?: string | null) => {
  const t = norm(type);
  const n = norm(name);
  return t === "water" || t === "voda" || t.includes("voda") || t.includes("water") || n.includes("voda") || n.includes("water");
};

const isWater2 = (name?: string | null) => norm(name).includes("voda 2");

/**
 * Flour & water fraction of a semi-finished / child recipe, expressed as a
 * fraction of the child's TOTAL mass — matching mes-pekara's starter
 * flour_fraction / water_fraction (flour_fraction + water_fraction ≈ 1).
 */
const childFractions = (child?: Recipe | null) => {
  const lines = child?.RecipeIngredient_RecipeToRecipeIngredient_Recipe || [];
  let flour = 0;
  let water = 0;
  let total = 0;
  for (const line of lines) {
    const pct = Number(line.BakerPercentage) || 0;
    if (pct === 0) continue;
    total += pct;
    const ing = line.Ingredient_IngredientToRecipeIngredient;
    const type = resolveType(ing);
    const name = ing?.IngredientName;
    if (isFlour(type) || isEnzyme(type)) flour += pct;
    else if (isWater(type, name)) water += pct;
  }
  if (total <= 0) return { flourFrac: 0, waterFrac: 0 };
  return { flourFrac: flour / total, waterFrac: water / total };
};

export interface RecipeMetrics {
  total_pct: number; // Sum of all baker percentages (Ukupni PEK%)
  flour_total: number; // Flour percentage incl. enzymes + starter flour (Brašno PEK%)
  water_total: number; // All water (voda1 + voda2 + starter water)
  hydration_phase_1: number; // (voda1 + starter water) / flour * 100
  total_hydration: number; // (voda1 + voda2 + starter water) / flour * 100
  valid: boolean;
  warnings: string[];
  errors: string[];
}

interface IngredientWithPercentage extends RecipeIngredient {
  Ingredient_IngredientToRecipeIngredient?: Ingredient;
}

/** mes-pekara rule: flour (Brašno PEK%) must total 100% ± 0.5%. */
function flourValidationErrors(flour_total: number): string[] {
  if (flour_total === 0) return ["Recept mora sadržavati brašno"];
  if (Math.abs(flour_total - 100) > 0.5) {
    return [
      `Brašno PEK% mora biti točno 100% (±0.5%) — trenutno ${flour_total.toFixed(2)}% (razlika ${
        flour_total - 100 >= 0 ? "+" : ""
      }${(flour_total - 100).toFixed(2)}%)`,
    ];
  }
  return [];
}

/** Flat ingredient line (trials / scratch recipes — no semi-finished children). */
export interface FlatLine {
  type?: string | null;
  name?: string | null;
  pct: number | string;
}

/**
 * Metrics for a flat ingredient list (no semi-finished/child recipes), using
 * the same classification as calcRecipeMetrics. Used by the trial screens.
 */
export function calcFlatMetrics(lines: FlatLine[]): RecipeMetrics {
  let flour_total = 0;
  let water1 = 0; // regular water (not "voda 2")
  let water2 = 0; // "voda 2"
  let total_pct = 0;

  for (const line of lines) {
    const pct = Number(line.pct) || 0;
    total_pct += pct;
    if (pct === 0) continue;
    if (isFlour(line.type) || isEnzyme(line.type)) {
      flour_total += pct;
    } else if (isWater(line.type, line.name)) {
      if (isWater2(line.name)) water2 += pct;
      else water1 += pct;
    }
  }

  const water_total = water1 + water2;
  const hydration_phase_1 = flour_total > 0 ? (water1 / flour_total) * 100 : 0;
  const total_hydration = flour_total > 0 ? (water_total / flour_total) * 100 : 0;

  const errors = flourValidationErrors(flour_total);

  return {
    total_pct,
    flour_total,
    water_total,
    hydration_phase_1,
    total_hydration,
    valid: errors.length === 0,
    warnings: [],
    errors,
  };
}

/**
 * Calculate recipe metrics from an ingredient/semi-finished line list.
 */
export function calcRecipeMetrics(
  ingredients: IngredientWithPercentage[]
): RecipeMetrics {
  let flour_total = 0;
  let water1 = 0; // regular water (not "voda 2")
  let water2 = 0; // "voda 2"
  let water_starter = 0; // water from child recipes (starters/bige/yudane)
  let total_pct = 0;

  for (const ing of ingredients) {
    const pct = Number(ing.BakerPercentage) || 0;
    total_pct += pct;
    if (pct === 0) continue;

    // Semi-finished / child recipe (starter, biga, yudane, seed_mix, ...)
    if (ing.ChildRecipe && ing.Recipe_RecipeToRecipeIngredient_ChildRecipe) {
      const child = ing.Recipe_RecipeToRecipeIngredient_ChildRecipe;
      const sfType = norm(child.SemiFinishedProduct?.SemiFinishedType);
      const { flourFrac, waterFrac } = childFractions(child);
      flour_total += pct * flourFrac;
      // seed_mix contributes neither flour nor water to hydration
      if (sfType !== "seed_mix") {
        water_starter += pct * waterFrac;
      }
      continue;
    }

    // Direct raw ingredient
    const def = ing.Ingredient_IngredientToRecipeIngredient;
    const type = resolveType(def);
    const name = def?.IngredientName;
    if (isFlour(type) || isEnzyme(type)) {
      flour_total += pct;
    } else if (isWater(type, name)) {
      if (isWater2(name)) water2 += pct;
      else water1 += pct;
    }
  }

  const water_total = water1 + water2 + water_starter;
  const water_phase1 = water1 + water_starter;
  const hydration_phase_1 = flour_total > 0 ? (water_phase1 / flour_total) * 100 : 0;
  const total_hydration = flour_total > 0 ? (water_total / flour_total) * 100 : 0;

  // VALIDATION — flour must be 100% ± 0.5% (mes-pekara rule)
  const errors = flourValidationErrors(flour_total);

  return {
    total_pct,
    flour_total,
    water_total,
    hydration_phase_1,
    total_hydration,
    valid: errors.length === 0,
    warnings: [],
    errors,
  };
}

/**
 * Get color for validation status
 */
export function getValidationColor(metrics: RecipeMetrics): string {
  if (metrics.errors.length > 0) return "#e74c3c"; // Red
  if (metrics.warnings.length > 0) return "#f39c12"; // Orange
  return "#27ae60"; // Green
}

/**
 * Get validation status text
 */
export function getValidationStatus(metrics: RecipeMetrics): string {
  if (metrics.errors.length > 0) return "Invalid";
  if (metrics.warnings.length > 0) return "Warning";
  return "Valid";
}

/**
 * Format percentage for display
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return value.toFixed(decimals) + "%";
}

/**
 * Categorize ingredient by type for validation
 */
export function categorizeIngredient(
  ingredient: Ingredient
): "flour" | "water" | "fat" | "other" {
  const type = ingredient.IngredientTypeName?.toLowerCase() || "";

  if (type.includes("flour") || type.includes("brasno")) return "flour";
  if (type.includes("water") || type.includes("voda")) return "water";
  if (
    type.includes("fat") ||
    type.includes("oil") ||
    type.includes("butter") ||
    type.includes("ulje") ||
    type.includes("mast")
  )
    return "fat";

  return "other";
}
